import type { Animal, Breed } from 'shared/@generated/models';
import { sanitizeQueryParams } from 'shared/library/api-client';
import { createFilterStore } from 'shared/state/create-filter-store';

export type BreedersFilter = {
  breeds: (number | string)[];
  page: number;
};

export type BreedersMapFilter = {
  breeds: number[];
  query: string;
  animal: Animal | null;
};

export const defaultBreedersFilter: BreedersFilter = {
  breeds: [],
  page: 1,
};

function normalizeBreederBreedValues(values: unknown): number[] {
  const items = Array.isArray(values)
    ? values
    : typeof values === 'string'
      ? values.split(',')
      : [];

  return Array.from(
    new Set(
      items
        .map((value) =>
          typeof value === 'number' || typeof value === 'string' ? Number(value) : NaN,
        )
        .filter((value) => Number.isSafeInteger(value) && value > 0),
    ),
  );
}

export function parseBreedersFilter(values: Partial<BreedersFilter>): BreedersFilter {
  const parsed = sanitizeQueryParams({ page: values.page }, { page: 1 });

  return {
    breeds: normalizeBreederBreedValues(values.breeds),
    page: typeof parsed.page === 'number' && parsed.page > 0 ? parsed.page : 1,
  };
}

export function parseBreedersMapFilter(values: Partial<BreedersMapFilter>): BreedersMapFilter {
  const animalValues = ['dog', 'cat'] satisfies Animal[];

  return {
    breeds: normalizeBreederBreedValues(values.breeds),
    query: typeof values.query === 'string' ? values.query : '',
    animal: animalValues.includes(values.animal as Animal) ? (values.animal as Animal) : null,
  };
}

export function getBreedersApiParams<T extends { breeds?: unknown }>(values: T) {
  const { breeds, ...params } = values;
  const breedIds = normalizeBreederBreedValues(breeds);

  return {
    ...params,
    ...(breedIds.length > 0 ? { breedIds } : {}),
  };
}

export function filterBreederBreedOptions(breeds: Breed[], animal: Animal | null, query: string) {
  const normalizedQuery = query.trim().toLocaleLowerCase();

  return breeds
    .filter((breed) => !animal || breed.animal === animal)
    .filter((breed) => !normalizedQuery || breed.name.toLocaleLowerCase().includes(normalizedQuery))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export const [BreedersFilterProvider, useBreedersFilterStore] =
  createFilterStore(defaultBreedersFilter);
